<template>
  <div class="relative w-full max-w-5xl mx-auto py-4 md:py-8">
    <!-- Main screenshot display -->
    <div class="relative">
      <!-- Loading indicator only shown on initial load -->
      <div 
        v-if="initialLoading" 
        class="absolute inset-0 flex items-center justify-center z-10 bg-black/20 rounded-xl"
      >
        <div class="animate-pulse text-white/70">Loading...</div>
      </div>
      
      <!-- Mobile device frame with screenshot -->
      <div class="mobile-device-showcase">
        <div class="device-frame">
          <div class="device-header">
            <div class="device-notch"></div>
          </div>
          
          <!-- Screenshots with crossfade - improved display -->
          <div class="device-screen">
            <!-- Current image with improved transition -->
            <transition name="fade-scale" mode="out-in">
              <img 
                :key="currentIndex"
                :src="`/images/screenshots/${screenshots[currentIndex].image}`" 
                :alt="generateAltText(screenshots[currentIndex])"
                class="screenshot-image"
                @load="handleImageLoaded"
                @error="handleImageError"
              />
            </transition>
          </div>
          
          <div class="device-home-button"></div>
        </div>
        
        <!-- Subtle reflection effect -->
        <div class="reflection-effect"></div>
      </div>
      
      <!-- Info section -->
      <div class="mt-4 md:mt-8 text-center px-4">
        <h3 class="text-lg md:text-xl lg:text-2xl font-bold mb-2 md:mb-3">{{ screenshots[currentIndex].title }}</h3>
        <p class="text-center max-w-2xl text-white/80 mx-auto text-sm md:text-base lg:text-lg">
          {{ screenshots[currentIndex].description }}
        </p>
      </div>
      
      <!-- Enhanced navigation arrows - moved closer to device -->
      <button 
        @click="prevSlide" 
        class="carousel-nav-button carousel-nav-prev"
        aria-label="Previous screenshot"
      >
        <div class="button-content">
          <i class="pi pi-chevron-left"></i>
        </div>
      </button>
      
      <button 
        @click="nextSlide" 
        class="carousel-nav-button carousel-nav-next"
        aria-label="Next screenshot"
      >
        <div class="button-content">
          <i class="pi pi-chevron-right"></i>
        </div>
      </button>
    </div>
    
    <!-- Indicator dots -->
    <div class="flex justify-center mt-4 md:mt-8 gap-2 md:gap-3">
      <button 
        v-for="(_, index) in screenshots" 
        :key="index"
        @click="goToSlide(index)"
        class="w-3 h-3 md:w-3 md:h-3 rounded-full transition-all"
        :class="index === currentIndex ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/60'"
        :aria-label="`Go to screenshot ${index + 1}`"
      ></button>
    </div>
    
    <!-- Preload next images (hidden) -->
    <div class="hidden">
      <img 
        v-for="index in preloadIndexes" 
        :key="`preload-${index}`"
        :src="`/images/screenshots/${screenshots[index].image}`"
        :alt="generateAltText(screenshots[index])"
      />
    </div>
    
    <!-- Debug info (only shown on error) -->
    <div v-if="imageError" class="mt-4 p-2 bg-red-600/20 text-white rounded text-sm">
      <p>Image failed to load: {{ imageError }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed, onBeforeUnmount, nextTick } from 'vue';

interface Screenshot {
  image: string;
  title: string;
  description: string;
}

const props = defineProps<{
  screenshots: Screenshot[];
  autoplay?: boolean;
  interval?: number;
}>();

const currentIndex = ref(0);
const initialLoading = ref(true);
let autoplayInterval: NodeJS.Timeout | null = null;
const imageError = ref('');
const loadedImages = ref(new Set<number>());

// Calculate indexes of images to preload (next and previous)
const preloadIndexes = computed(() => {
  const total = props.screenshots.length;
  if (total <= 1) return [];
  
  const nextIndex = (currentIndex.value + 1) % total;
  const prevIndex = (currentIndex.value - 1 + total) % total;
  
  // Return unique indices excluding current
  return [nextIndex, prevIndex].filter(idx => idx !== currentIndex.value);
});

const goToSlide = (index: number) => {
  if (index === currentIndex.value) return;
  
  // If we've already loaded this image, we can switch immediately
  if (loadedImages.value.has(index)) {
    currentIndex.value = index;
  } else {
    // Otherwise show loading indicator while we wait for the image
    initialLoading.value = true;
    
    // Preload the target image then switch
    const img = new Image();
    img.src = `/images/screenshots/${props.screenshots[index].image}`;
    img.onload = () => {
      currentIndex.value = index;
      loadedImages.value.add(index);
      initialLoading.value = false;
    };
    // Fixed error handler type compatibility
    img.onerror = () => {
      handleImageError(`Failed to load image (${img.src})`);
    };
  }
  
  // Reset autoplay when manually changing slides
  if (props.autoplay) {
    stopAutoplay();
    startAutoplay();
  }
};

const nextSlide = () => {
  goToSlide((currentIndex.value + 1) % props.screenshots.length);
};

const prevSlide = () => {
  goToSlide((currentIndex.value - 1 + props.screenshots.length) % props.screenshots.length);
};

const handleImageLoaded = () => {
  loadedImages.value.add(currentIndex.value);
  initialLoading.value = false;
  
  // Let's make sure any custom image handling is applied after load
  nextTick(() => {
    const img = document.querySelector(`.screenshot-image[alt="${props.screenshots[currentIndex.value].title}"]`);
    if (img) {
      // Any additional post-load image adjustments could go here
    }
  });
};

// Updated to handle different types of error events
const handleImageError = (e: Event | string | unknown) => {
  if (typeof e === 'string') {
    imageError.value = `Failed to load image: ${e}`;
  } else if (e instanceof Event && e.target instanceof HTMLImageElement) {
    imageError.value = `Failed to load image (${e.target.src})`;
    console.error('Image failed to load:', e.target.src);
  } else {
    imageError.value = `Failed to load image (unknown error)`;
  }
  initialLoading.value = false;
};

const startAutoplay = () => {
  if (props.autoplay && !autoplayInterval) {
    autoplayInterval = setInterval(() => {
      nextSlide();
    }, props.interval || 5000);
  }
};

const stopAutoplay = () => {
  if (autoplayInterval) {
    clearInterval(autoplayInterval);
    autoplayInterval = null;
  }
};

onMounted(() => {
  // Start loading first image
  const preloadImage = new Image();
  preloadImage.src = `/images/screenshots/${props.screenshots[currentIndex.value].image}`;
  preloadImage.onload = () => {
    loadedImages.value.add(currentIndex.value);
    initialLoading.value = false;
  };
  // Fixed error handler type compatibility
  preloadImage.onerror = () => {
    handleImageError(`Failed to preload image (${preloadImage.src})`);
  };
  
  // Start autoplay if enabled
  startAutoplay();
});

onBeforeUnmount(() => {
  stopAutoplay();
});

// Reset autoplay when component visibility changes
watch(() => props.autoplay, (newVal) => {
  if (newVal) {
    startAutoplay();
  } else {
    stopAutoplay();
  }
});

// Generate descriptive alt text with keywords for better SEO and accessibility
const generateAltText = (screenshot: Screenshot) => {
  return `Payment Reminder app screenshot: ${screenshot.title} - ${screenshot.description.split('.')[0]}`;
};
</script>

<style scoped>
/* Mobile device frame styling - improved for full image display */
.mobile-device-showcase {
  position: relative;
  width: 260px;
  height: 520px;
  margin: 0 auto;
  perspective: 1500px;
  transform-style: preserve-3d;
  transform: rotateY(-2deg) rotateX(2deg);
  transition: transform 0.7s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.mobile-device-showcase:hover {
  transform: rotateY(0deg) rotateX(0deg);
}

.device-frame {
  position: relative;
  width: 100%;
  height: 100%;
  background: transparent;
  border-radius: 36px;
  box-shadow: 
    0 0 0 2px rgba(255, 255, 255, 0.12),
    0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(5px);
}

.device-header {
  height: 24px;
  width: 100%;
  background: rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;
}

.device-notch {
  position: absolute;
  width: 120px;
  height: 20px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 0 0 12px 12px;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
}

.device-screen {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Improved screenshot image display */
.screenshot-image {
  width: 100%;
  height: 100%;
  object-fit: contain; /* This ensures the full image is displayed */
  object-position: center;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
}

.device-home-button {
  height: 4px;
  width: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  margin: 10px auto;
}

.reflection-effect {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 5%,
    rgba(255, 255, 255, 0) 20%
  );
  pointer-events: none;
  border-radius: 36px;
}

/* Smooth fade-scale transition for images */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: absolute;
  width: 100%;
  height: 100%;
}

.fade-scale-enter-from {
  opacity: 0;
  transform: scale(0.92);
}

.fade-scale-leave-to {
  opacity: 0;
  transform: scale(1.06);
}

.fade-scale-enter-to,
.fade-scale-leave-from {
  opacity: 1;
  transform: scale(1);
}

/* Enhanced navigation buttons */
.carousel-nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 1rem;
  color: white;
}

.carousel-nav-button:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.carousel-nav-button:active {
  transform: translateY(-50%) scale(0.95);
  background: rgba(255, 255, 255, 0.3);
}

.carousel-nav-prev {
  left: -16px;
}

.carousel-nav-next {
  right: -16px;
}

/* Responsive adjustments - fixed and consolidated breakpoints */
@media (min-width: 640px) {
  .mobile-device-showcase {
    width: 280px;
    height: 560px;
    transform: rotateY(-3deg) rotateX(3deg);
  }
  
  .carousel-nav-prev {
    left: 10px; /* Moved closer to the device */
  }
  
  .carousel-nav-next {
    right: 10px; /* Moved closer to the device */
  }
}

@media (min-width: 768px) {
  .mobile-device-showcase {
    width: 300px;
    height: 600px;
    transform: rotateY(-4deg) rotateX(4deg);
  }
  
  .carousel-nav-button {
    width: 56px;
    height: 56px;
    font-size: 1.25rem;
  }
  
  .carousel-nav-prev {
    left: 20px; /* Moved closer to the device */
  }
  
  .carousel-nav-next {
    right: 20px; /* Moved closer to the device */
  }
}

@media (min-width: 1024px) {
  .mobile-device-showcase {
    width: 340px;
    height: 680px;
    transform: rotateY(-5deg) rotateX(5deg);
  }
  
  .carousel-nav-button {
    width: 64px;
    height: 64px;
    font-size: 1.5rem;
  }
  
  .carousel-nav-prev {
    left: 40px; /* Positioned closer to the device for desktop */
  }
  
  .carousel-nav-next {
    right: 40px; /* Positioned closer to the device for desktop */
  }
}

@media (min-width: 1280px) {
  .carousel-nav-button {
    width: 70px;
    height: 70px;
  }
}
</style>
